/**
 * ContentFlag request schemas — shared between the API (server-side request
 * validation) and the web SPA (client-side form validation).
 *
 * Spec §7 Flagging Model, §10 Approval Gate, §19 ContentFlag:
 *   - A `ContentFlag` is attached to a `ContentVersion`. Flags carry both
 *     manually-raised issues (operator review) and machine-raised issues
 *     (AI Judge, analytics). The same wire shape is used by both — only
 *     `source` differs.
 *   - Flags are immutable except for resolution metadata (`resolvedAt`,
 *     `resolutionNote`). The resolution lifecycle is: created → resolved
 *     (timestamp set, optional note) → optionally un-resolved (timestamp
 *     cleared back to `null`). The flag body itself never changes after
 *     creation.
 *   - The publish-gate check (issue #12) reads unresolved HIGH/CRITICAL
 *     flags for a version and refuses to publish unless overridden. This
 *     file does NOT implement that gate — it only owns the CRUD shapes.
 *     Issue #12 owns the publish-side query and 422 mapping.
 *
 * Slice boundary (issue #14):
 *   - This file is the canonical source for flag request / response shapes.
 *   - Lives in its own file (not `content.ts`) because Dev A is touching
 *     `content.ts` in parallel for #12; keeping flags isolated avoids any
 *     shared-schema merge conflict.
 *
 * Validation rules:
 *   - `source`, `reason`, `severity` must match the Prisma enums (`FlagSource`,
 *     `FlagReason`, `FlagSeverity`). Anything else → 422.
 *   - `message` is required, trimmed, 1..2000 chars. We cap at 2000 to keep
 *     a reasonable upper bound on rendered flag panels and to head off
 *     pathological inputs.
 *   - `suggestion` is optional, trimmed, max 4000 chars when present.
 *   - `resolutionNote` is optional, trimmed, max 4000 chars.
 *   - `resolvedAt` is an ISO-8601 timestamp string OR `null` (to un-resolve a
 *     previously-resolved flag). Empty body / missing fields are rejected:
 *     PATCH must include at least one of `resolvedAt` / `resolutionNote`.
 */

import { z } from "zod";
import type { FlagReason, FlagSeverity, FlagSource } from "../types/index.js";

/**
 * Mirror of `FlagSource` in `shared/src/types/index.ts` and the `FlagSource`
 * Prisma enum. Centralised here so Zod can enumerate the literal values at
 * runtime.
 */
export const flagSourceSchema = z.enum([
  "USER",
  "AI_JUDGE",
  "SYSTEM",
  "ANALYTICS",
]) satisfies z.ZodType<FlagSource>;

/**
 * Mirror of `FlagReason` in `shared/src/types/index.ts` and the `FlagReason`
 * Prisma enum.
 */
export const flagReasonSchema = z.enum([
  "ACCURACY",
  "READABILITY",
  "AI_SLOP",
  "SEO",
  "BRAND_TONE",
  "OUTDATED",
  "BROKEN_LINK",
  "COMPLIANCE",
  "IMAGE_LICENCE",
  "LOW_PERFORMANCE",
  "OTHER",
]) satisfies z.ZodType<FlagReason>;

/**
 * Mirror of `FlagSeverity` in `shared/src/types/index.ts` and the
 * `FlagSeverity` Prisma enum.
 */
export const flagSeveritySchema = z.enum([
  "LOW",
  "MEDIUM",
  "HIGH",
  "CRITICAL",
]) satisfies z.ZodType<FlagSeverity>;

/**
 * Body schema for `POST /api/admin/content-versions/:id/flags`.
 *
 * `.strict()` rejects unknown top-level keys so a typo like `serverity`
 * surfaces as a 422 instead of being silently dropped.
 */
export const createContentFlagSchema = z
  .object({
    source: flagSourceSchema,
    reason: flagReasonSchema,
    severity: flagSeveritySchema,
    message: z.string().trim().min(1, "Message is required").max(2000),
    suggestion: z.string().trim().max(4000).optional(),
  })
  .strict();
export type CreateContentFlagInput = z.infer<typeof createContentFlagSchema>;

/**
 * Query-parameter schema for `GET /api/admin/content-versions/:id/flags`.
 * Standard offset pagination per `standards/api-patterns.md` (default 20,
 * max 100). The list returns BOTH unresolved and resolved flags ordered by
 * `createdAt DESC`; clients filter by `resolvedAt` as needed.
 */
export const listContentFlagsQuerySchema = z
  .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict();
export type ListContentFlagsQuery = z.infer<typeof listContentFlagsQuerySchema>;

/**
 * Body schema for `PATCH /api/admin/flags/:id`.
 *
 * Both fields are independently optional, but the patch must include at
 * least one of them — an empty object would be a no-op and is rejected so
 * the operator surface always reflects a deliberate state change.
 *
 * `resolvedAt` accepts either:
 *   - an ISO-8601 datetime string (the operator marks the flag resolved at
 *     the supplied instant), or
 *   - `null` (the operator un-resolves a previously-resolved flag — both
 *     `resolvedAt` and the persisted state revert to "open").
 *
 * `resolutionNote` is the freeform operator note explaining why the flag
 * was resolved. It is independently patchable from `resolvedAt` so an
 * operator can annotate an already-resolved flag without re-stamping its
 * resolution timestamp.
 */
export const updateContentFlagSchema = z
  .object({
    resolvedAt: z.union([z.string().trim().datetime({ offset: true }), z.null()]).optional(),
    resolutionNote: z.string().trim().max(4000).optional(),
  })
  .strict()
  .refine((obj) => Object.keys(obj).length > 0, {
    message: "Update must include at least one field",
  });
export type UpdateContentFlagInput = z.infer<typeof updateContentFlagSchema>;

/**
 * Public-facing ContentFlag shape. ISO-string timestamps for transport;
 * never leaks Prisma-specific internal fields.
 */
export type ContentFlagResponse = {
  id: string;
  contentVersionId: string;
  source: FlagSource;
  reason: FlagReason;
  severity: FlagSeverity;
  message: string;
  suggestion: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  createdAt: string;
};
